import type {
  AgentEvent,
  ConnectorContent,
  ConnectorEntry,
  ConnectorInfo,
  ConnectorResponseEvent,
  ConnectorStatusEvent,
  ListOptions,
  MountInfo,
  SearchOptions,
} from "@skaile/workspaces/types";
import type {
  ConnectorStatus,
  ResourceClientSnapshot,
  StoreTransport,
  SubscribableStore,
} from "./types.js";

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Lazy resource browser for agent connectors.
 *
 * Provides a reactive list of connected mounts and connectors, and
 * request/response methods for browsing connector contents. Shares
 * the same transport (`send` + `onEvent`) as `AgentStore`.
 * @docLink packages/store/concepts#resource-client
 */
export class ResourceClient implements SubscribableStore<ResourceClientSnapshot> {
  private readonly _send: StoreTransport["send"];
  private _mounts: readonly MountInfo[] = [];
  private _connectors: readonly ConnectorInfo[] = [];

  /** Live connection status per connector, keyed by connector ID. Updated by connector_status events. */
  connectorStatus: Map<string, ConnectorStatus> = new Map();

  private _snapshot: ResourceClientSnapshot | null = null;
  private readonly _listeners = new Set<() => void>();
  private readonly _pendingRequests = new Map<
    string,
    {
      resolve: (event: ConnectorResponseEvent) => void;
      reject: (error: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  private readonly _unsubEvent: () => void;

  /**
   * @param transport - Shared transport (same instance as the AgentStore for this session).
   */
  constructor(transport: StoreTransport) {
    this._send = transport.send;
    this._unsubEvent = transport.onEvent((event) => this._handleEvent(event));
  }

  // -- Reactive state --------------------------------------------------------

  /** Mounts available in this session, populated from `resources_available` events. */
  get mounts(): readonly MountInfo[] {
    return this._mounts;
  }

  /** Tool-based connectors available in this session, populated from `resources_available` events. */
  get connectors(): readonly ConnectorInfo[] {
    return this._connectors;
  }

  // -- Resource operations ---------------------------------------------------

  /**
   * List directory entries at the given path within a connector.
   *
   * @param connectorId - ID of the connector to query.
   * @param path - Directory path to list. Omit for the connector root.
   * @param opts - Optional pagination and filter options.
   * @returns Array of entries (files, directories, etc.) at the given path.
   * @throws If the request times out or the connector returns an error.
   */
  async list(connectorId: string, path?: string, opts?: ListOptions): Promise<ConnectorEntry[]> {
    const response = await this._request(connectorId, "list", path, opts);
    return (response.data as ConnectorEntry[]) ?? [];
  }

  /**
   * Read the content of a single resource.
   *
   * @param connectorId - ID of the connector to query.
   * @param path - Full path to the resource within the connector.
   * @returns The resource content object.
   * @throws If the request times out or the connector returns an error.
   */
  async read(connectorId: string, path: string): Promise<ConnectorContent> {
    const response = await this._request(connectorId, "read", path);
    return response.data as ConnectorContent;
  }

  /**
   * Write content to a resource, creating it if it does not exist.
   *
   * @param connectorId - ID of the connector to write to.
   * @param path - Full path to the resource within the connector.
   * @param content - Content to write.
   * @throws If the request times out or the connector returns an error.
   */
  async write(connectorId: string, path: string, content: ConnectorContent): Promise<void> {
    await this._request(connectorId, "write", path, undefined, content);
  }

  /**
   * Delete a resource from a connector.
   *
   * @param connectorId - ID of the connector to delete from.
   * @param path - Full path to the resource within the connector.
   * @throws If the request times out or the connector returns an error.
   */
  async delete(connectorId: string, path: string): Promise<void> {
    await this._request(connectorId, "delete", path);
  }

  /**
   * Full-text search within a connector.
   *
   * @param connectorId - ID of the connector to search.
   * @param query - Search query string.
   * @param opts - Optional search options (e.g. path prefix, result limit).
   * @returns Matching entries, ordered by relevance as determined by the connector.
   * @throws If the request times out or the connector returns an error.
   */
  async search(
    connectorId: string,
    query: string,
    opts?: SearchOptions,
  ): Promise<ConnectorEntry[]> {
    const response = await this._request(connectorId, "search", query, opts);
    return (response.data as ConnectorEntry[]) ?? [];
  }

  // -- useSyncExternalStore interface ----------------------------------------

  /**
   * Register a change listener. Called after every resource availability change.
   *
   * Conforms to the `subscribe` parameter of React's `useSyncExternalStore`.
   *
   * @param listener - Callback invoked whenever mounts or connectors change.
   * @returns Unsubscribe function — call to stop receiving notifications.
   */
  subscribe = (listener: () => void): (() => void) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  /**
   * Return the current immutable resource snapshot.
   *
   * A new object is produced lazily after each `resources_available` event.
   * Conforms to the `getSnapshot` parameter of React's `useSyncExternalStore`.
   */
  getSnapshot = (): ResourceClientSnapshot => {
    if (!this._snapshot) {
      this._snapshot = { mounts: this._mounts, connectors: this._connectors };
    }
    return this._snapshot;
  };

  // -- Connector config mutations --------------------------------------------

  /**
   * Send a `connector_config` add command to wire a new connector at runtime,
   * without restarting the session. The runner receives this command and
   * calls `ConnectorManager.connect()`, which fires a `connector_status` event
   * back to the store once the connector is live.
   *
   * @param declaration - Connector declaration (id, driver, access level, options).
   */
  addConnector(declaration: {
    id: string;
    driver: string;
    access?: "read-only" | "read-write";
    options?: Record<string, unknown>;
  }): void {
    this._send({ type: "connector_config", action: "add", declaration });
  }

  /**
   * Send a `connector_config` remove command to disconnect a connector by ID
   * without restarting the session.
   *
   * @param connectorId - ID of the connector to disconnect and remove.
   */
  removeConnector(connectorId: string): void {
    this._send({ type: "connector_config", action: "remove", connectorId });
  }

  /**
   * Send a `connector_config` refresh command to reconnect all configured
   * connectors (e.g. after a credentials change).
   */
  refreshConnectors(): void {
    this._send({ type: "connector_config", action: "refresh" });
  }

  // -- Cleanup ---------------------------------------------------------------

  /**
   * Unsubscribe from transport events, reject all pending requests, and
   * clear all change listeners.
   *
   * All in-flight `list`/`read`/`write`/`delete`/`search` calls will reject
   * with `"ResourceClient disposed"`.
   */
  dispose(): void {
    this._unsubEvent();
    for (const [, pending] of this._pendingRequests) {
      clearTimeout(pending.timer);
      pending.reject(new Error("ResourceClient disposed"));
    }
    this._pendingRequests.clear();
    this._listeners.clear();
  }

  // -- Internal --------------------------------------------------------------

  private _handleEvent(event: AgentEvent): void {
    if (event.type === "connector_status") {
      const e = event as ConnectorStatusEvent;
      const existing = this.connectorStatus.get(e.connectorId);
      const next = new Map(this.connectorStatus);
      next.set(e.connectorId, {
        id: e.connectorId,
        driver:
          existing?.driver ?? this._connectors.find((c) => c.id === e.connectorId)?.driver ?? "",
        state: e.status,
        ...(e.error !== undefined ? { error: e.error } : {}),
        ...(e.latencyMs !== undefined ? { latencyMs: e.latencyMs } : {}),
        lastUpdated: new Date(),
      });
      this.connectorStatus = next;
      for (const listener of this._listeners) {
        listener();
      }
      return;
    }

    if (event.type === "resources_available") {
      this._mounts = event.mounts;
      this._connectors = event.connectors;
      this._snapshot = null;
      for (const listener of this._listeners) {
        listener();
      }
      return;
    }

    if (event.type === "resource_response") {
      const pending = this._pendingRequests.get(event.requestId);
      if (!pending) return;

      clearTimeout(pending.timer);
      this._pendingRequests.delete(event.requestId);

      if (event.error) {
        pending.reject(new Error(event.error));
      } else {
        pending.resolve(event);
      }
    }
  }

  private _request(
    connectorId: string,
    operation: "list" | "read" | "write" | "delete" | "search",
    path?: string,
    options?: ListOptions | SearchOptions,
    content?: ConnectorContent,
  ): Promise<ConnectorResponseEvent> {
    const requestId = crypto.randomUUID();

    return new Promise<ConnectorResponseEvent>((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pendingRequests.delete(requestId);
        reject(
          new Error(
            `Resource request timed out after ${DEFAULT_TIMEOUT_MS}ms: ${operation} ${connectorId}${path ? ` ${path}` : ""}`,
          ),
        );
      }, DEFAULT_TIMEOUT_MS);

      this._pendingRequests.set(requestId, { resolve, reject, timer });

      this._send({
        type: "resource_request",
        requestId,
        connectorId,
        operation,
        ...(path !== undefined ? { path } : {}),
        ...(options ? { options } : {}),
        ...(content ? { content } : {}),
      });
    });
  }
}
